import { useTranslation } from 'react-i18next'
import { ArrowForwardIos as ArrowForwardIosIcon } from '@mui/icons-material'
import { Box, Stack, Typography } from '@mui/material'
import PropTypes from 'prop-types'
import { Dialog } from '~/components'

const HTML_FIELDS = new Set(['title', 'text', 'icon_caption'])
const IMAGE_FIELDS = new Set(['media', 'icon'])

const fieldLeaf = (field) => field.split('.').pop()

const toYoutubeEmbedUrl = (value) => {
  if (typeof value !== 'string' || !value) {
    return null
  }

  if (/youtube\.com\/embed\//.test(value)) {
    return value
  }

  const watchMatch = value.match(/youtube\.com\/watch\?v=([^&]+)/)
  if (watchMatch) {
    return `https://www.youtube.com/embed/${watchMatch[1]}`
  }

  const shortMatch = value.match(/youtu\.be\/([^?&]+)/)
  if (shortMatch) {
    return `https://www.youtube.com/embed/${shortMatch[1]}`
  }

  return null
}

const YoutubeEmbed = ({ src, url }) => (
  <Stack spacing={0.5}>
    <Box
      sx={{
        aspectRatio: '16 / 9',
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        overflow: 'hidden',
      }}
    >
      <iframe
        allowFullScreen
        src={src}
        style={{ border: 'none', height: '100%', width: '100%' }}
        title=""
      />
    </Box>
    <Typography
      color="text.secondary"
      sx={{ wordBreak: 'break-all' }}
      variant="caption"
    >
      {url}
    </Typography>
  </Stack>
)

YoutubeEmbed.propTypes = {
  src: PropTypes.string.isRequired,
  url: PropTypes.string.isRequired,
}

const fieldLabel = (field, t) => {
  const leaf = fieldLeaf(field)
  const label = t(`paragraph.${leaf.replace(/_/g, '-')}`, {
    defaultValue: leaf,
  })

  if (field.startsWith('shops.')) {
    const [, shop] = field.split('.')

    return `${shop}: ${label}`
  }

  return label
}

const DiffValue = ({ field, value }) => {
  const { t } = useTranslation()
  if (value === null || value === undefined || value === '') {
    return (
      <Typography
        color="text.secondary"
        sx={{ fontStyle: 'italic' }}
        variant="body2"
      >
        —
      </Typography>
    )
  }

  const leaf = fieldLeaf(field)

  if (leaf === 'skip') {
    return (
      <Typography variant="body2">
        {value ? t('ui.yes') : t('ui.no')}
      </Typography>
    )
  }

  if (
    HTML_FIELDS.has(leaf) &&
    typeof value === 'string' &&
    value.includes('<')
  ) {
    return (
      <Box
        sx={{
          typography: 'body2',
          '& > *:first-of-type': { mt: 0 },
          '& > *:last-child': { mb: 0 },
        }}
        dangerouslySetInnerHTML={{ __html: value }}
      />
    )
  }

  if (IMAGE_FIELDS.has(leaf) && typeof value === 'string') {
    const youtubeEmbed = leaf === 'media' ? toYoutubeEmbedUrl(value) : null

    if (youtubeEmbed) {
      return <YoutubeEmbed src={youtubeEmbed} url={value} />
    }

    if (
      leaf === 'media' &&
      (value.endsWith('.mp4') || value.endsWith('.webm'))
    ) {
      return (
        <Box
          autoPlay
          component="video"
          loop
          muted
          playsInline
          src={value}
          sx={{
            display: 'block',
            maxWidth: '100%',
            maxHeight: 200,
          }}
        />
      )
    }

    return (
      <Box
        alt=""
        component="img"
        src={value}
        sx={{
          display: 'block',
          maxWidth: '100%',
          maxHeight: 200,
          objectFit: 'contain',
        }}
      />
    )
  }

  return <Typography variant="body2">{String(value)}</Typography>
}

DiffValue.propTypes = {
  field: PropTypes.string.isRequired,
  value: PropTypes.any,
}

const RestoreFromParentDialog = (props) => {
  const { diff, open, onClose, onConfirm, ...rest } = props
  const { t } = useTranslation()

  const entries = diff ? Object.entries(diff) : []

  return (
    <Dialog
      {...rest}
      actions={[
        { id: 'cancel', name: t('ui.cancel'), variant: 'outlined' },
        {
          id: 'confirm',
          name: t('paragraph.restore-diff.confirm'),
          variant: 'contained',
        },
      ]}
      body={
        <Stack spacing={3}>
          {entries.map(([field, [parent, current]]) => (
            <Stack key={field} spacing={1}>
              <Typography fontWeight={600} variant="subtitle2">
                {fieldLabel(field, t)}
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Box
                  sx={{
                    flex: 1,
                    p: 1.5,
                    borderRadius: 1,
                    border: 1,
                    borderColor: 'divider',
                    bgcolor: 'action.hover',
                  }}
                >
                  <Typography
                    color="text.secondary"
                    display="block"
                    gutterBottom
                    variant="caption"
                  >
                    {t('paragraph.restore-diff.parent')}
                  </Typography>
                  <DiffValue field={field} value={parent} />
                </Box>
                <ArrowForwardIosIcon sx={{ alignSelf: 'center' }} />
                <Box
                  sx={{
                    flex: 1,
                    p: 1.5,
                    borderRadius: 1,
                    border: 1,
                    borderColor: 'divider',
                  }}
                >
                  <Typography
                    color="text.secondary"
                    display="block"
                    gutterBottom
                    variant="caption"
                  >
                    {t('paragraph.restore-diff.current')}
                  </Typography>
                  <DiffValue field={field} value={current} />
                </Box>
              </Stack>
            </Stack>
          ))}
        </Stack>
      }
      fullWidth
      maxWidth="md"
      open={open}
      title={t('paragraph.restore-diff.title')}
      onClose={(action) => {
        if (action === 'confirm') {
          onConfirm?.()
        }
        onClose?.()
      }}
    />
  )
}

RestoreFromParentDialog.propTypes = {
  diff: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.any)),
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func,
}

export default RestoreFromParentDialog
